import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Check,
  Camera,
  UserPlus,
  Zap,
  Trash2,
  Loader2,
  ChevronLeft,
  ShieldCheck,
  Circle,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MASTER_PIN = "1111";
const TARGET_PHOTOS = 5;

/**
 * Multi-person sandbox for PayPunch face-ID testing.
 *   VIEW 'roster'  – list of test employees + add-new form
 *   VIEW 'person'  – 3 actions for one selected person:
 *                       Enroll face · Punch In · Delete
 */
export default function SandboxOnboardPage() {
  const [view, setView] = useState("roster"); // 'roster' | 'person'
  const [roster, setRoster] = useState([]);
  const [current, setCurrent] = useState(null); // {code,name,enrolled,photo_count}
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const loadRoster = async () => {
    try {
      const res = await axios.get(`${API}/dev/test-employees`);
      setRoster(res.data.employees || []);
    } catch (e) {
      toast.error("Couldn't load test employees");
    }
  };

  useEffect(() => {
    loadRoster();
  }, []);

  const addPerson = async () => {
    const name = (newName || "").trim() || `Test Person ${roster.length + 1}`;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/dev/seed-test-employee`, { params: { name } });
      toast.success(`✅ Added ${res.data.name} (#${res.data.code})`);
      setNewName("");
      await loadRoster();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const deletePerson = async (code) => {
    if (!window.confirm("Remove this test employee and all their sandbox data?")) return;
    setBusy(true);
    try {
      await axios.get(`${API}/dev/wipe-one-test-employee`, { params: { code } });
      toast.success("Removed");
      if (current?.code === code) {
        setCurrent(null);
        setView("roster");
      }
      await loadRoster();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const wipeAll = async () => {
    if (!window.confirm("Wipe ALL test employees, faces, and sandbox punches?")) return;
    setBusy(true);
    try {
      await axios.get(`${API}/dev/wipe-test-employees`);
      await axios.delete(`${API}/kiosk-face/test-punches`);
      toast.success("Sandbox cleared");
      setCurrent(null);
      setView("roster");
      await loadRoster();
    } catch (e) {
      toast.error("Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  const openPerson = (p) => {
    setCurrent(p);
    setView("person");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f10] via-[#1a1a1c] to-[#0f0f10] text-white px-4 py-8 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6" data-testid="sandbox-header">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-[11px] font-mono tracking-wider mb-3">
            <Zap className="w-3 h-3" /> SANDBOX · REAL PAYROLL UNTOUCHED
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">PayPunch Test Drive</h1>
          <p className="text-gray-400 mt-2 text-sm">
            Add as many test people as you like, enroll their faces, punch as them, then wipe when done.
          </p>
        </header>

        {view === "roster" && (
          <RosterView
            roster={roster}
            busy={busy}
            newName={newName}
            setNewName={setNewName}
            onAdd={addPerson}
            onOpen={openPerson}
            onDelete={deletePerson}
            onWipeAll={wipeAll}
          />
        )}

        {view === "person" && current && (
          <PersonView
            person={current}
            onBack={async () => {
              await loadRoster();
              setView("roster");
              setCurrent(null);
            }}
          />
        )}

        <footer className="mt-8 text-center text-xs text-gray-500">
          Master PIN: <span className="font-mono text-gray-400">{MASTER_PIN}</span> · Test codes live in the <span className="font-mono">9990–9999</span> range
        </footer>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ROSTER VIEW – list + add
// ═══════════════════════════════════════════════════════════
function RosterView({ roster, busy, newName, setNewName, onAdd, onOpen, onDelete, onWipeAll }) {
  return (
    <>
      <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-5 mb-4" data-testid="roster-add-card">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="w-5 h-5 text-blue-300" />
          <h2 className="text-lg font-semibold">Add a test person</h2>
        </div>
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAdd()}
            placeholder="Full name (e.g. Alice Test)"
            className="bg-[#252527] border-[#3a3a3c] text-white placeholder:text-gray-500 h-11"
            data-testid="input-new-name"
          />
          <Button
            onClick={onAdd}
            disabled={busy}
            className="bg-blue-500 hover:bg-blue-600 text-white h-11 px-5 shrink-0"
            data-testid="btn-add-person"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          A code from 9999 → 9990 is auto-assigned. Max 10 test people at a time.
        </p>
      </Card>

      <div className="space-y-2 mb-4">
        {roster.length === 0 && (
          <Card className="bg-[#1a1a1c] border-[#2a2a2c] border-dashed p-8 text-center">
            <p className="text-gray-400 text-sm">No test people yet. Add one above to get started.</p>
          </Card>
        )}
        {roster.map((p) => (
          <Card
            key={p.code}
            className="bg-[#1a1a1c] border-[#2a2a2c] p-4 flex items-center gap-3 hover:border-[#3a3a3c] transition-colors"
            data-testid={`roster-row-${p.code}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-mono text-xs
              ${p.enrolled ? "bg-green-500/20 text-green-300" : "bg-gray-700/40 text-gray-400"}`}>
              #{p.code}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              <div className="text-[11px] text-gray-500 flex items-center gap-1.5 mt-0.5">
                {p.enrolled ? (
                  <><ShieldCheck className="w-3 h-3 text-green-400" /> Enrolled ({p.photo_count} baseline photos)</>
                ) : (
                  <><Circle className="w-3 h-3 text-amber-400" /> Not enrolled · {p.photo_count}/{TARGET_PHOTOS} photos</>
                )}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => onOpen(p)}
              className="bg-blue-500 hover:bg-blue-600 text-white h-9"
              data-testid={`btn-open-${p.code}`}
            >
              Open
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(p.code)}
              className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0 shrink-0"
              data-testid={`btn-del-${p.code}`}
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </Card>
        ))}
      </div>

      {roster.length > 0 && (
        <Button
          onClick={onWipeAll}
          disabled={busy}
          variant="outline"
          className="w-full bg-[#252527] border-red-500/30 text-red-300 hover:bg-red-500/10 h-11"
          data-testid="btn-wipe-all"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Wipe entire sandbox
        </Button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-6">
        {[
          { href: "/test-punches", label: "Sandbox punches" },
          { href: "/schedule", label: "Schedule" },
          { href: "/metrics", label: "Metrics" },
          { href: "/payroll", label: "Payroll" },
        ].map((l) => (
          <a key={l.href} href={l.href} target="_blank" rel="noreferrer">
            <Button
              variant="outline"
              className="w-full h-10 bg-[#1a1a1c] border-[#2a2a2c] text-gray-300 hover:bg-[#252527] text-xs"
              data-testid={`link-${l.href.replace("/", "")}`}
            >
              {l.label}
              <ExternalLink className="w-3 h-3 ml-1.5" />
            </Button>
          </a>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// PERSON VIEW – enroll + punch for one selected person
// ═══════════════════════════════════════════════════════════
function PersonView({ person, onBack }) {
  const [mode, setMode] = useState(person.enrolled ? "punch" : "enroll"); // 'enroll' | 'punch'
  const [photos, setPhotos] = useState([]);
  const [videoOn, setVideoOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enrolled, setEnrolled] = useState(person.enrolled);
  const [lastResult, setLastResult] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setVideoOn(true);
    } catch {
      toast.error("Camera blocked — allow access and reload.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setVideoOn(false);
  };

  useEffect(() => stopCamera, []);

  const snap = () => {
    if (!videoRef.current) return null;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.75);
  };

  const enrollShot = async () => {
    if (!videoOn) return startCamera();
    const photo = snap();
    if (!photo) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/kiosk-face/enroll/${person.code}`,
        { photo_base64: photo },
        { headers: { "X-Master-Code": MASTER_PIN } }
      );
      setPhotos((p) => [...p, photo]);
      toast.success(`Photo ${res.data.photo_count}/${TARGET_PHOTOS}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enroll failed");
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/kiosk-face/enroll/${person.code}/finalize`,
        {},
        { headers: { "X-Master-Code": MASTER_PIN } }
      );
      toast.success(`✅ ${person.name} enrolled`);
      setEnrolled(true);
      setMode("punch");
      setPhotos([]);
      stopCamera();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finalize failed");
    } finally {
      setBusy(false);
    }
  };

  const doPunch = async (punch_type) => {
    if (!videoOn) return startCamera();
    const photo = snap();
    if (!photo) return;
    setBusy(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/kiosk-face/punch`, {
        code: person.code,
        photo_base64: photo,
        punch_type,
        test_mode: true,
      });
      setLastResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Punch failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={onBack}
        className="text-gray-400 hover:text-white mb-3 pl-1"
        data-testid="btn-back-roster"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Back to roster
      </Button>

      <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-5 mb-4">
        <div className="flex items-center gap-3 mb-1">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center font-mono text-xs
            ${enrolled ? "bg-green-500/20 text-green-300" : "bg-gray-700/40 text-gray-400"}`}>
            #{person.code}
          </div>
          <div>
            <h2 className="text-xl font-semibold">{person.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {enrolled ? "Face enrolled — ready to punch" : "Not enrolled yet"}
            </p>
          </div>
        </div>

        <div className="flex gap-1 mt-4 bg-[#252527] p-1 rounded-lg">
          <button
            onClick={() => setMode("enroll")}
            className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors
              ${mode === "enroll" ? "bg-[#1a1a1c] text-white" : "text-gray-500 hover:text-gray-300"}`}
            data-testid="tab-enroll"
          >
            {enrolled ? "Re-enroll" : "Enroll face"}
          </button>
          <button
            onClick={() => setMode("punch")}
            disabled={!enrolled}
            className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors
              ${mode === "punch" ? "bg-[#1a1a1c] text-white" : "text-gray-500 hover:text-gray-300"}
              ${!enrolled ? "opacity-40 cursor-not-allowed" : ""}`}
            data-testid="tab-punch"
          >
            Punch
          </button>
        </div>
      </Card>

      <Card className="bg-[#1a1a1c] border-[#2a2a2c] p-5">
        <div className="rounded-lg overflow-hidden bg-black aspect-[4/3] mb-4 border border-[#2a2a2c] flex items-center justify-center">
          {videoOn ? (
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
          ) : (
            <div className="text-gray-500 text-sm px-4 text-center">
              Camera off — tap the button below to start
            </div>
          )}
        </div>

        {mode === "enroll" && (
          <>
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-gray-400">Photos captured</span>
              <span className="font-mono text-white">{photos.length} / {TARGET_PHOTOS}</span>
            </div>
            <div className="w-full h-1.5 bg-[#2a2a2c] rounded-full mb-4 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                style={{ width: `${(photos.length / TARGET_PHOTOS) * 100}%` }}
              />
            </div>
            {photos.length < TARGET_PHOTOS ? (
              <Button
                onClick={enrollShot}
                disabled={busy}
                size="lg"
                className="w-full bg-purple-500 hover:bg-purple-600 text-white h-13 py-3.5"
                data-testid="btn-enroll-shot"
              >
                {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Camera className="w-5 h-5 mr-2" />}
                {videoOn ? `Capture photo ${photos.length + 1}` : "Start Camera"}
              </Button>
            ) : (
              <Button
                onClick={finalize}
                disabled={busy}
                size="lg"
                className="w-full bg-green-500 hover:bg-green-600 text-white h-13 py-3.5"
                data-testid="btn-finalize"
              >
                {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
                Finalize enrollment
              </Button>
            )}
            <p className="text-[11px] text-gray-500 mt-2 text-center">
              Vary angles and expressions — better baseline = fewer false blocks later.
            </p>
          </>
        )}

        {mode === "punch" && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Button
                onClick={() => doPunch("in")}
                disabled={busy || !enrolled}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white h-13 py-3.5"
                data-testid="btn-punch-in"
              >
                {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Zap className="w-5 h-5 mr-2" />}
                Punch In
              </Button>
              <Button
                onClick={() => doPunch("out")}
                disabled={busy || !enrolled}
                size="lg"
                variant="outline"
                className="bg-[#252527] border-[#3a3a3c] text-white hover:bg-[#2f2f31] h-13 py-3.5"
                data-testid="btn-punch-out"
              >
                Punch Out
              </Button>
            </div>
            {!videoOn && (
              <Button
                onClick={startCamera}
                variant="outline"
                className="w-full bg-[#252527] border-[#3a3a3c] text-gray-300 hover:bg-[#2f2f31] h-10"
                data-testid="btn-start-camera-punch"
              >
                <Camera className="w-4 h-4 mr-2" /> Start Camera
              </Button>
            )}

            {lastResult && (
              <div
                className={`mt-4 p-3 rounded-lg border text-sm
                  ${lastResult.success
                    ? "bg-green-500/10 border-green-500/30 text-green-300"
                    : "bg-red-500/10 border-red-500/30 text-red-300"}`}
                data-testid="punch-result"
              >
                <div className="font-semibold mb-1">
                  {lastResult.success ? "✅ Match" : "❌ Face mismatch"}
                </div>
                <div className="text-xs opacity-80">
                  Confidence: <span className="font-mono">{lastResult.confidence}/10</span>
                  {lastResult.reason && <> · {lastResult.reason}</>}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <p className="text-center text-[11px] text-gray-500 mt-6">
        All punches from this page land in <span className="font-mono">test_time_punches</span> (sandbox only). Real payroll is untouched.
      </p>
    </>
  );
}
